import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import FormInput from '../../../shared/components/FormInput';
import { fetchWithAuth } from '../../auth/fetchWithAuth';
import RoleSelector from './RoleSelector';
import { useCurrentUser } from '../../auth/CurrentUserContext';
import { USER_ROLES } from '../../auth/permissions';
import { API_URL } from '../../../config/env';

export default function InviteUserModal({ show, onClose, onInvited }) {
  const { user: currentUser } = useCurrentUser();
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(USER_ROLES.ENGINEER);
  const [error, setError] = useState(null);

  const handleInvite = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/users/invitation`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user: {
              email,
              role,
            },
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.errors?.join(', ') || res.statusText || 'Invitation failed');
      }

      setEmail('');
      setRole(USER_ROLES.ENGINEER);
      onInvited();
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <button type="button" className="btn btn-link text-muted" onClick={onClose} disabled={submitting}>
        <i className="bi bi-arrow-left me-1" /> Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleInvite}
        disabled={!email || submitting}
      >
        Send Invite
      </button>
    </div>
  );

  return (
    <AdminModal show={show} onClose={onClose} title="Invite User" footer={footer}>
      <form>
        <FormInput
          id="invite_email"
          type="email"
          label="Email address"
          placeholder="name@company.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={submitting}
          required
        />

        <div className="mb-3">
          <label htmlFor="invite_role" className="form-label">Role</label>
          <RoleSelector actor={currentUser} value={role} onChange={setRole} disabled={submitting} />
        </div>
        {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
      </form>
    </AdminModal>
  );
}

InviteUserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInvited: PropTypes.func,
};

InviteUserModal.defaultProps = {
  onInvited: () => {},
};
